import React from 'react';
import { Network, Wifi, Shield, Cloud, HardDrive, Headphones, Building, CheckCircle, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';

const Services = () => {
  const services = [
    {
      icon: <Globe className="w-10 h-10" />,
      title: 'Website Design & Hosting',
      description:
        'Modern, professional website design and reliable hosting for small to mid-sized businesses. We create beautiful, SEO-optimized websites that drive results.',
      features: [
        'Custom responsive web design',
        'SEO optimization',
        'Affordable hosting packages',
        'Content management systems',
        'E-commerce solutions',
      ],
      image: 'https://images.unsplash.com/photo-1467232004584-a241de8bcf5d',
    },
    {
      icon: <Network className="w-10 h-10" />,
      title: 'Office Network Architecture & Implementation',
      description:
        'Custom-designed network infrastructure built for your business. We plan, implement, and optimize office networks for maximum performance, security, and scalability.',
      features: [
        'Network design and planning',
        'Structured cabling solutions',
        'Router and switch configuration',
        'VLAN segmentation',
        'Network performance optimization',
      ],
      image: 'https://images.unsplash.com/photo-1594915440248-1e419eba6611',
    },
    {
      icon: <Wifi className="w-10 h-10" />,
      title: 'Secure Wi-Fi Design & Optimization',
      description:
        'Enterprise-grade wireless networks that provide seamless coverage, robust security, and reliable connectivity throughout your office space.',
      features: [
        'Wi-Fi site surveys and planning',
        'Access point installation',
        'Guest network setup',
        'WPA3 security implementation',
        'Coverage optimization',
      ],
      image: 'https://images.unsplash.com/photo-1551434678-e076c223a692',
    },
    {
      icon: <Headphones className="w-10 h-10" />,
      title: 'System Administration & End-User Support',
      description:
        'Comprehensive IT support to keep your business running smoothly. Our team handles daily administration, troubleshooting, and user assistance.',
      features: [
        '24/7 help desk support',
        'User account management',
        'Software installation and updates',
        'Hardware troubleshooting',
        'Remote and on-site support',
      ],
      image: 'https://images.pexels.com/photos/1181336/pexels-photo-1181336.jpeg',
    },
    {
      icon: <Cloud className="w-10 h-10" />,
      title: 'Microsoft 365 Setup, Migration & Support',
      description:
        'Complete Microsoft 365 services including setup, migration from legacy systems, user training, and ongoing administration.',
      features: [
        'Microsoft 365 tenant setup',
        'Email migration (Exchange, Gmail)',
        'SharePoint and Teams configuration',
        'User training and documentation',
        'Security and compliance setup',
      ],
      image: 'https://images.unsplash.com/photo-1632910061325-10116ac047e3',
    },
    {
      icon: <HardDrive className="w-10 h-10" />,
      title: 'Cloud & Hybrid Infrastructure Planning',
      description:
        'Strategic planning and implementation of cloud and hybrid infrastructure solutions tailored to your business needs and budget.',
      features: [
        'Cloud migration strategy',
        'Azure and AWS setup',
        'Hybrid infrastructure design',
        'Backup and disaster recovery',
        'Cost optimization',
      ],
      image: 'https://images.unsplash.com/photo-1521737711867-e3b97375f902',
    },
    {
      icon: <Shield className="w-10 h-10" />,
      title: 'Endpoint Protection & Cybersecurity Services',
      description:
        'Comprehensive security solutions to protect your business from cyber threats, including endpoint protection, firewalls, and security monitoring.',
      features: [
        'Endpoint security deployment',
        'Firewall configuration',
        'Security monitoring and alerts',
        'Vulnerability assessments',
        'Security policy development',
      ],
      image: 'https://images.pexels.com/photos/577585/pexels-photo-577585.jpeg',
    },
    {
      icon: <Building className="w-10 h-10" />,
      title: 'IT Consulting for New Office Builds or Expansions',
      description:
        'Expert IT consulting for new office construction or expansions. We work with contractors and architects to ensure proper technology integration.',
      features: [
        'IT infrastructure planning',
        'Technology requirements analysis',
        'Vendor coordination',
        'Project management',
        'Post-deployment support',
      ],
      image: 'https://images.pexels.com/photos/1181335/pexels-photo-1181335.jpeg',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-gray-50">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">Our Services</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive IT solutions designed to support and scale with your business
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="space-y-16">
            {services.map((service, index) => (
              <div
                key={index}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:grid-flow-dense' : ''
                }`}
              >
                <div className={`space-y-6 ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                  <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center text-[#2995FF]">
                    {service.icon}
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900">{service.title}</h2>
                  <p className="text-lg text-gray-600 leading-relaxed">{service.description}</p>
                  <div className="space-y-3">
                    {service.features.map((feature, fIndex) => (
                      <div key={fIndex} className="flex items-start space-x-3">
                        <CheckCircle className="text-[#2995FF] mt-1 flex-shrink-0" size={18} />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className={index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}>
                  <div className="bg-gradient-to-br from-blue-50 to-gray-50 rounded-2xl p-12 border border-gray-200">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="rounded-xl shadow-lg w-full h-auto object-cover"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#f2f2f2]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Process</h2>
            <p className="text-xl text-gray-600">How we deliver exceptional IT solutions</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Consultation', description: 'We assess your needs and goals' },
              { step: '02', title: 'Planning', description: 'Custom solution design and proposal' },
              { step: '03', title: 'Implementation', description: 'Expert deployment and configuration' },
              { step: '04', title: 'Support', description: 'Ongoing maintenance and optimization' },
            ].map((item, index) => (
              <div key={index} className="text-center space-y-4">
                <div className="text-5xl font-bold text-[#2995FF] opacity-20">{item.step}</div>
                <h3 className="text-xl font-bold text-gray-900">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-[#2995FF] to-[#004AAD]">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-4xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-xl mb-8 text-blue-100">
            Let's discuss how our services can benefit your business. Get a free consultation and quote today.
          </p>
          <Link
            to="/request-quote"
            className="inline-block px-8 py-4 bg-white text-[#2995FF] rounded-lg font-semibold hover:bg-gray-100 shadow-lg"
          >
            Request a Quote
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
